import { api } from "@/api/client";
import type { ResolveRequest } from "@/api/order_check";

/**
 * Раздел «Что это?» — `backend/src/wh_helper/label_info/schemas.py`.
 * Раздел только читает: ни БД портала, ни 1С не меняет.
 */

export interface RefName {
  ref: string;
  name: string;
}

export interface LabelInfoConfig {
  scan_method: string;
}

export interface LabelScanItem {
  nomenclature_ref: string;
  name: string;
  article: string;
  /** `null` — характеристика не определилась (здесь это не вопрос). */
  characteristic: RefName | null;
  partia: RefName | null;
  /** Базовые штуки; у коробки — весь объём позиции. */
  qty: number;
}

/** Короб/паллета с упаковки (chz-station) — `PackingOut` бэка. */
export interface LabelPacking {
  kind: "box" | "pallet";
  /** SSCC короба / код паллеты. */
  code: string;
  /** Место упаковки (`line-1`, `opt-table`…). */
  station: string;
  packed_at: string | null;
  packed_by_name: string;
  /** Паллета, на которой лежит короб; пусто — не на паллете (только у короба). */
  pallet_code: string;
  /** Коробов на паллете (только у паллеты). */
  boxes_count: number;
  /** Кодов штук (КИ) в коробе / во всех коробах паллеты. */
  codes_count: number;
  revision: number;
  /** Из них вынуто складом (скан кода штуки в любом разделе). */
  taken_count: number;
  /** Когда короб вскрыт (у паллеты — самый ранний её короб); `null` — целый. */
  opened_at: string | null;
}

export interface LabelScan {
  raw: string;
  kind: string;
  source: string;
  /** Не опознано — `false` и текст резолвера в `message` (ответ 200). */
  matched: boolean;
  message: string;
  /** Предупреждение к успешному скану: вскрытый короб упаковки — «Грузоместо
   *  неполное: было N, осталось M». Пусто — нечего сказать. */
  notice: string;
  sscc: string;
  box: { id: string; sscc: string; status: string } | null;
  /** Короб/паллета с упаковки (`source == "packing"`), иначе `null`. */
  packing: LabelPacking | null;
  items: LabelScanItem[];
}

export type UnitKind = "warehouse" | "department" | "counterparty" | "other";

export interface StockRow {
  organization: RefName;
  unit: RefName;
  unit_kind: UnitKind;
  characteristic: RefName | null;
  partia: RefName | null;
  total: number;
  free: number;
  reserved: number;
}

export interface NomenclatureInfo {
  nomenclature: { ref: string; name: string; article: string; unit: RefName | null };
  /** Эхо переданной характеристики — для подсветки строк остатка. */
  characteristic: RefName | null;
  stock: {
    rows: StockRow[];
    total: number;
    free: number;
    reserved: number;
    on_warehouses: number;
    by_organization: { organization: RefName; total: number; free: number; reserved: number }[];
  };
  consumption: {
    days: number;
    period_from: string;
    period_to: string;
    total: number;
    by_organization: { organization: RefName; qty: number }[];
    by_doc: Record<string, number>;
  };
  /** Остаток на складах / среднедневной расход; `null` — расхода не было. */
  days_of_cover: number | null;
  warnings: string[];
}

export async function getLabelInfoConfig(): Promise<LabelInfoConfig> {
  const { data } = await api.get<LabelInfoConfig>("/label-info/config");
  return data;
}

export async function scanLabel(body: ResolveRequest): Promise<LabelScan> {
  const { data } = await api.post<LabelScan>("/label-info/scan", body);
  return data;
}

export async function getNomenclatureInfo(
  ref: string,
  characteristicRef: string,
): Promise<NomenclatureInfo> {
  const { data } = await api.get<NomenclatureInfo>(
    `/label-info/nomenclature/${encodeURIComponent(ref)}`,
    { params: characteristicRef ? { characteristic_ref: characteristicRef } : {} },
  );
  return data;
}
